/**
 * Country-keyed fun facts shown during itinerary generation.
 * Keys match destinationThemes.js (lowercase country names).
 */

const DESTINATION_FUN_FACTS = {
  japan: [
    "Japan has over 6,800 islands, but most people live on just four of them.",
    "Vending machines in Japan sell everything from hot soup to fresh eggs.",
    "Japan's rail system is so punctual, delays of over a minute come with apology slips.",
    "There are more pets than children in Japan — cats and dogs outnumber kids under 15.",
    "Kit-Kats come in over 200 regional flavors across Japan, including wasabi and sake.",
  ],
  india: [
    "India has the world's largest postal network with over 155,000 post offices.",
    "The game of chess was invented in India during the Gupta Empire around the 6th century.",
    "India's Kumbh Mela gathering is so massive it's visible from space.",
    "Meghalaya's living root bridges grow stronger with age — some are over 500 years old.",
    "India produces more films each year than Hollywood, with over 1,500 annually.",
  ],
  iceland: [
    "Iceland has no mosquitoes — one of the few inhabited places on Earth without them.",
    "About 60% of Iceland's population lives in the Reykjavík capital area.",
    "Iceland heats 90% of its homes using geothermal energy from volcanic activity.",
    "The Icelandic language has barely changed since the 13th century — locals can read medieval sagas.",
    "There are no forests in Iceland — Vikings cut them all down 1,000 years ago.",
  ],
  france: [
    "France is the most visited country on Earth with roughly 90 million tourists per year.",
    "There are over 1,600 types of cheese produced in France.",
    "The Louvre would take 100 days to see every piece if you spent 30 seconds on each.",
    "France spans 12 time zones — more than any other country — thanks to overseas territories.",
    "The French invented the hot air balloon, the parachute, and the Braille writing system.",
  ],
  italy: [
    "Italy has more UNESCO World Heritage Sites than any other country in the world.",
    "The University of Bologna, founded in 1088, is the oldest university in continuous operation.",
    "Italians eat about 25 kg of pasta per person every year.",
    "Rome's Trevi Fountain collects roughly €3,000 in coins every day from tourist wishes.",
    "Italy has a free wine fountain in Ortona that dispenses red wine 24/7.",
  ],
  spain: [
    "Spain's La Tomatina festival uses over 150,000 tomatoes in a single hour.",
    "Madrid's Sobrino de Botín, open since 1725, is the world's oldest restaurant.",
    "Spain produces about half of the world's olive oil each year.",
    "The tradition of the siesta is fading — only 18% of Spaniards nap regularly now.",
    "Spain has over 8,000 km of coastline and more than 300 days of sunshine a year.",
  ],
  thailand: [
    "Thailand is the only Southeast Asian country never colonized by a European power.",
    "Bangkok's full ceremonial name has 168 characters — the longest city name in the world.",
    "Thailand has over 40,000 Buddhist temples, with about 35,000 still active.",
    "The Thai alphabet has 44 consonants, 15 vowel symbols, and 4 tone marks.",
    "Red Bull (Krating Daeng) was invented in Thailand in 1976.",
  ],
  mexico: [
    "Mexico City is built on a lake — it sinks about 10 inches every year.",
    "Mexico introduced chocolate, chili peppers, and corn to the rest of the world.",
    "The world's smallest volcano, Cuexcomate at 13 meters tall, is in Puebla, Mexico.",
    "Mexico has 35 UNESCO World Heritage Sites, more than any country in the Americas.",
    "Cinco de Mayo is barely celebrated in Mexico — it's bigger in the United States.",
  ],
  egypt: [
    "The Great Pyramid of Giza was the tallest structure on Earth for 3,800 years.",
    "Ancient Egyptians invented toothpaste, paper, door locks, and the 365-day calendar.",
    "Cleopatra lived closer in time to the Moon landing than to the building of the pyramids.",
    "Egypt's Suez Canal handles about 12% of all global trade shipping.",
    "The ancient city of Cairo is nicknamed 'The City of a Thousand Minarets.'",
  ],
  brazil: [
    "Brazil contains about 60% of the Amazon rainforest — the world's largest tropical forest.",
    "São Paulo has over 12,000 restaurants, making it one of the world's top food cities.",
    "Brazil is named after the brazilwood tree, once its most valuable export.",
    "Brazil has won the FIFA World Cup five times — more than any other nation.",
    "The Amazon River discharges more water than the next seven largest rivers combined.",
  ],
  morocco: [
    "Morocco is home to the world's oldest university, the University of al-Qarawiyyin (founded 859 AD).",
    "The blue city of Chefchaouen was painted blue by Jewish refugees in the 1930s.",
    "Morocco is the world's largest exporter of phosphates, used in fertilizers worldwide.",
    "Moroccan mint tea is poured from a height to create a frothy top — it's an art form.",
    "The Sahara Desert covers most of southern Morocco and grows about 48 km southward each year.",
  ],
  australia: [
    "Australia is wider than the Moon — 4,000 km vs the Moon's 3,400 km diameter.",
    "The Great Barrier Reef is the largest living structure on Earth, visible from space.",
    "Australia has over 10,000 beaches — you could visit a new one every day for 27 years.",
    "Kangaroos and emus can't walk backward, which is why they're on Australia's coat of arms.",
    "Australia was the second country in the world to give women the right to vote (1902).",
  ],
  'south korea': [
    "South Korea's internet speed is consistently among the fastest in the world.",
    "Koreans add one or two years to their age at birth — everyone ages up on New Year's Day.",
    "South Korea has a separate Valentine's Day each month, including Black Day for singles.",
    "Kimchi has over 200 varieties, and Koreans eat about 40 pounds of it per person yearly.",
    "Seoul's Gangnam district inspired the viral hit that became YouTube's first billion-view video.",
  ],
  turkey: [
    "Turkey spans two continents — Europe and Asia — with Istanbul straddling the divide.",
    "Tulips actually originated in Turkey, not the Netherlands, and were exported to Europe.",
    "Turkey has more ancient Greek ruins than Greece itself.",
    "Turkish people consume more tea per capita than any other nation — about 7 pounds per year.",
    "The Grand Bazaar in Istanbul has over 4,000 shops and is one of the world's oldest malls.",
  ],
  greece: [
    "Greece has more archaeological museums than any other country in the world.",
    "The Greek language has been spoken for over 3,400 years — one of the oldest living languages.",
    "Greece has over 6,000 islands, but only about 227 are inhabited.",
    "Ancient Greeks invented the Olympic Games in 776 BC as a tribute to Zeus.",
    "No point in Greece is more than 137 km from the sea.",
  ],
  peru: [
    "Machu Picchu was built without mortar — the stones fit together so tightly you can't slide paper between them.",
    "Peru has over 3,000 varieties of potatoes — more than anywhere else on Earth.",
    "The Nazca Lines in Peru are giant geoglyphs only visible from the air, and no one knows exactly why they were made.",
    "Lake Titicaca, shared with Bolivia, is the highest navigable lake in the world at 3,812 meters.",
    "Peru's Rainbow Mountain was hidden under glacial ice until it melted and was discovered around 2015.",
  ],
  'new zealand': [
    "New Zealand was the first country to give all women the right to vote in 1893.",
    "There are about 6 sheep for every person in New Zealand.",
    "New Zealand has no native land mammals — its original wildlife was almost entirely birds.",
    "Hobbiton from The Lord of the Rings still exists as a permanent attraction in Matamata.",
    "New Zealand's Fiordland gets up to 8 meters of rainfall per year — one of the wettest places on Earth.",
  ],
  canada: [
    "Canada has more lakes than all other countries combined — over 60% of the world's lakes.",
    "The Trans-Canada Highway stretches over 7,800 km — one of the longest national highways.",
    "Canada produces about 71% of the world's maple syrup, mostly from Quebec.",
    "Churchill, Manitoba is called the 'Polar Bear Capital of the World' — bears outnumber people.",
    "Canada's coastline is the longest in the world at over 243,000 km.",
  ],
  nepal: [
    "Nepal is home to 8 of the world's 14 peaks above 8,000 meters, including Everest.",
    "Nepal's flag is the only national flag that isn't rectangular — it has two stacked triangles.",
    "Lumbini in Nepal is the birthplace of Siddhartha Gautama (the Buddha).",
    "Nepal has over 120 ethnic groups and 123 languages spoken across the country.",
    "The Nepali calendar is about 56 years ahead of the Western Gregorian calendar.",
  ],
  indonesia: [
    "Indonesia has over 17,000 islands — making it the world's largest archipelago.",
    "Komodo dragons, the world's largest lizards, are found only in Indonesia.",
    "Indonesia is home to the world's largest flower (Rafflesia) and the tallest flower (Amorphophallus).",
    "Bali alone has over 20,000 Hindu temples, roughly one for every 200 residents.",
    "Indonesia spans three time zones and sits on the Pacific Ring of Fire with 130+ active volcanoes.",
  ],
};

const DEFAULT_FACTS = [
  "Traveling to a new country can boost creativity — studies show novel experiences enhance cognitive flexibility.",
  "The world's most visited cities include Bangkok, Paris, and London, each welcoming over 20 million visitors yearly.",
  "Learning even five words in the local language can transform how locals interact with you.",
  "The best travel photos are often taken during 'golden hour' — the first and last hour of sunlight.",
  "Packing cubes can save up to 30% of suitcase space and keep everything organized.",
  "Jet lag hits harder traveling east because your body finds it easier to stay up late than wake up early.",
];

/**
 * Returns fun facts for a given country, falling back to generic travel facts.
 * @param {string} country
 * @returns {string[]}
 */
export const getFactsForCountry = (country) => {
  if (!country) return DEFAULT_FACTS;
  const key = country.toLowerCase().trim();
  return DESTINATION_FUN_FACTS[key] || DEFAULT_FACTS;
};

export { DEFAULT_FACTS };
export default DESTINATION_FUN_FACTS;
